import type { Round, RoundSummary } from '../api/types';

/**
 * Client-side aggregation. There is no aggregate endpoint, so every number on Início and
 * Estatísticas is computed here from the rounds the client already holds.
 *
 * Rule inherited from the design: a statistic that has no data is `null`, never 0.
 * "6 de 18" and "—" mean different things and the UI must be able to tell them apart.
 */

export interface GameSummary {
  roundCount: number;
  /** Lowest total strokes across completed rounds. */
  bestScore: number | null;
  averageScore: number | null;
  averageToPar: number | null;
  bestDifferential: number | null;
  /** Needs round detail; null when only summaries are loaded. */
  averageGirPct: number | null;
  averagePuttsPerRound: number | null;
  averagePuttsPerHole: number | null;
  averageFairwayPct: number | null;
}

function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function defined<T>(values: Array<T | null | undefined>): T[] {
  return values.filter((v): v is T => v != null);
}

/**
 * Everything derivable from round *summaries* alone — cheap, available immediately.
 */
export function summariseRounds(rounds: RoundSummary[]): GameSummary {
  const strokes = defined(rounds.map((r) => r.totalStrokes));
  const toPar = defined(rounds.map((r) => r.totalToPar));
  const diffs = defined(rounds.map((r) => r.scoreDifferential));

  return {
    roundCount: rounds.length,
    bestScore: strokes.length > 0 ? Math.min(...strokes) : null,
    averageScore: mean(strokes),
    averageToPar: mean(toPar),
    bestDifferential: diffs.length > 0 ? Math.min(...diffs) : null,
    averageGirPct: null,
    averagePuttsPerRound: null,
    averagePuttsPerHole: null,
    averageFairwayPct: null,
  };
}

/**
 * Folds round *details* in, adding the statistics that only exist on the detail DTO.
 * Safe to call with a partial set — it averages whatever has arrived.
 */
export function withDetailStats(base: GameSummary, details: Round[]): GameSummary {
  if (details.length === 0) return base;

  const gir = details.map((r) => r.stats.greensInRegulationPct);
  const fairway = details.map((r) => r.stats.fairwayHitPct);
  const putts = defined(details.map((r) => r.stats.totalPutts));
  const puttsPerHole = defined(details.map((r) => r.stats.averagePuttsPerHole));

  return {
    ...base,
    averageGirPct: mean(gir),
    averageFairwayPct: mean(fairway),
    averagePuttsPerRound: mean(putts),
    averagePuttsPerHole: mean(puttsPerHole),
  };
}

/**
 * How many more completed rounds until WHS produces an index.
 *
 * The backend's table starts at 3 rounds (lowest differential − 2.0); with 0–2 it returns
 * null. That threshold is what the new-account state on Início counts down to.
 */
export const MIN_ROUNDS_FOR_HANDICAP = 3;

export function roundsUntilHandicap(completedCount: number): number {
  return Math.max(0, MIN_ROUNDS_FOR_HANDICAP - completedCount);
}

/**
 * The eight differentials that actually define the index.
 *
 * WHS uses the best 8 of the last 20; below 20 rounds it uses fewer scores and applies an
 * adjustment. Returning the *set* lets the differentials chart mark which points count and
 * which are discarded — the canvas makes that distinction the whole point of the chart.
 */
export function countingDifferentials(rounds: RoundSummary[]): Set<string> {
  const recent = rounds
    .filter((r) => r.status === 'completed' && r.scoreDifferential != null)
    .sort(
      (a, b) =>
        new Date(b.completedAt ?? b.startedAt).getTime() -
        new Date(a.completedAt ?? a.startedAt).getTime(),
    )
    .slice(0, 20);

  const take = countingScoreCount(recent.length);
  if (take === 0) return new Set();

  return new Set(
    [...recent]
      .sort((a, b) => (a.scoreDifferential ?? 0) - (b.scoreDifferential ?? 0))
      .slice(0, take)
      .map((r) => r.id),
  );
}

/**
 * How many of the lowest differentials the WHS table uses, by round count.
 * Mirrors the backend's handicap service exactly — if these ever disagree, the chart
 * would highlight different rounds than the index actually used.
 */
export function countingScoreCount(n: number): number {
  if (n < 3) return 0;
  if (n <= 5) return 1;
  if (n <= 8) return 2;
  if (n <= 11) return 3;
  if (n <= 14) return 4;
  if (n <= 16) return 5;
  if (n <= 18) return 6;
  if (n === 19) return 7;
  return 8;
}

/** The adjustment the WHS table applies on top of the average, for the profile explainer. */
export function handicapAdjustment(n: number): number {
  if (n === 3) return -2.0;
  if (n === 4) return -1.0;
  if (n === 6) return -1.0;
  return 0;
}
